import { useEffect, useMemo, useRef, useState } from 'react';
import { useCerbos } from '@cerbos/react';
import type { ClientWithPrincipal } from '@cerbos/core';
import useDeepCompareEffect from 'react-use/esm/useDeepCompareEffect';
import useNetworkState from 'react-use/esm/useNetworkState';

import { canonicalJson } from './canonical-json';
import type { BaseAccessCheckResult, CerbosActionType } from './types';
import type { ResourceConfig } from './utils';
import { createResourceCheck, getPermissions } from './utils';

/**
 * Resources per `checkResources` request — the PDP's own cap
 * (`maxResourcesPerRequest` in Cerbos.Config's `.cerbos.yaml`, 75 on every
 * PDP). A request over it is rejected as a whole.
 */
export const MAX_RESOURCES_PER_REQUEST = 75;

/** One entity of a list, checked under its own id. */
export interface CerbosItemInput {
  id: number | string;
  /**
   * Sent to Cerbos as the resource's attributes, for policy conditions that
   * decide per entity. Part of the decision key: an entity whose attributes
   * change is checked again.
   */
  attributes?: Record<string, string | number | boolean>;
}

/** Options for {@link useCerbosItemsAccessCheck}. */
export interface CerbosItemsAccessCheckOptions<TPermission extends string> {
  /** The Cerbos resource kind every item is checked as. */
  kind: string;
  /**
   * Permission name → action(s). Keep it a module-level constant: a decision
   * already made is not re-checked when the map changes.
   */
  actionMap: Record<TPermission, CerbosActionType | CerbosActionType[]>;
  /** The entities to check. */
  items: readonly CerbosItemInput[];
  /** Cerbos resource scope. Part of the decision key. */
  scope?: string;
}

/** Result of {@link useCerbosItemsAccessCheck}. */
export interface CerbosItemsAccessCheckResult<
  TPermission extends string,
> extends BaseAccessCheckResult {
  /**
   * Keyed by the item's id (as a string), for every item passed in.
   * All-`false` for an item whose check hasn't resolved yet, or failed.
   */
  permissions: Record<string, Record<TPermission, boolean>>;
}

interface PendingCheck {
  key: string;
  id: string;
  attributes: CerbosItemInput['attributes'];
}

/**
 * What is known so far — only ever valid for the client (principal) that
 * learned it: decisions by key, and the error of every key whose last request
 * failed.
 */
interface DecisionCache<TPermission extends string> {
  client: ClientWithPrincipal;
  decisions: ReadonlyMap<string, Record<TPermission, boolean>>;
  failures: ReadonlyMap<string, Error>;
}

/** Everything a decision depends on besides the principal (and the constant `actionMap`). */
const toDecisionKey = (
  { id, attributes }: CerbosItemInput,
  kind: string,
  scope: string | undefined,
) => canonicalJson([kind, scope ?? null, String(id), attributes ?? null]);

const chunk = <T>(items: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_unused, index) =>
    items.slice(index * size, (index + 1) * size),
  );

const toError = (err: unknown) =>
  err instanceof Error ? err : new Error('An unexpected error occurred', { cause: err });

/**
 * Per-entity permissions for a whole list: each item is checked under its own
 * id, with its own attributes, so a per-resource condition in the policy
 * decides per row instead of the frontend second-guessing it.
 *
 * Pass every item the operator can reach — the whole filtered list, not just
 * the rows on screen. Decisions are cached per kind, scope, item and
 * attributes (and dropped when the principal changes), so paging, scrolling
 * and filters that only narrow the list cost no request and render every row's
 * actions at once. Only items without a decision yet are requested, in batches
 * of at most {@link MAX_RESOURCES_PER_REQUEST} sent in parallel, so a list
 * longer than the PDP accepts per request still resolves.
 *
 * Meant for lists of up to a few hundred items: every item is checked up
 * front, in `ceil(n / 75)` requests sent at once, so that any row scrolled
 * into view already has its decision. 1 000 items would mean 14 concurrent
 * PDP requests on first render, and the slowest of them holds `isLoading`.
 * Past that size, check the rows in view (or a page of them) instead.
 *
 * Permissions are keyed by id, so an id passed more than once is checked once,
 * with the attributes of its last occurrence — the one its permissions belong
 * to.
 *
 * A failed batch leaves its items denied, fail-closed, and its `error` stays
 * exposed until those items are decided — another batch succeeding does not
 * clear it. Failed items are requested again when the set of items (or the
 * kind, scope, principal or connectivity) changes, not on every render; while
 * they are, `isLoading` is `true` and their old error is gone.
 *
 * @example
 * ```ts
 * const actionMap = { canEdit: 'update', canDelete: 'delete' };
 * const { permissions } = useCerbosItemsAccessCheck({
 *   kind: 'Role',
 *   actionMap,
 *   items: roles.map(({ id, name }) => ({ id, attributes: { name } })),
 * });
 * permissions[String(role.id)]?.canEdit;
 * ```
 */
export function useCerbosItemsAccessCheck<TPermission extends string>({
  kind,
  actionMap,
  items,
  scope,
}: CerbosItemsAccessCheckOptions<TPermission>): CerbosItemsAccessCheckResult<TPermission> {
  const client = useCerbos();
  const { online } = useNetworkState();
  const [cache, setCache] = useState<DecisionCache<TPermission>>(() => ({
    client,
    decisions: new Map(),
    failures: new Map(),
  }));
  // Keys with a request in flight, so a re-run of the effect below doesn't
  // request them twice.
  const inFlightKeysRef = useRef(new Set<string>());
  const requestScopeRef = useRef<{ client: ClientWithPrincipal; signal: AbortSignal }>(undefined);

  // In-flight batches outlive a change of `items` — their decisions are still
  // worth caching — and are only aborted on unmount, or when the principal
  // (and so the client) changes and they'd answer for the wrong one.
  useEffect(() => {
    const abortController = new AbortController();
    const inFlightKeys = inFlightKeysRef.current;
    requestScopeRef.current = { client, signal: abortController.signal };
    return () => {
      abortController.abort();
      inFlightKeys.clear();
    };
  }, [client]);

  const isCurrent = cache.client === client;
  const decisions = isCurrent ? cache.decisions : undefined;
  const failures = isCurrent ? cache.failures : undefined;

  // Serialised once per new `items`, not on every render: pass a stable array
  // (one that only changes with its contents) to keep it that way.
  const { checks, checkKeys } = useMemo(() => {
    const uniqueItems = [...new Map(items.map((item) => [String(item.id), item])).values()];
    const built: PendingCheck[] = uniqueItems.map((item) => ({
      key: toDecisionKey(item, kind, scope),
      id: String(item.id),
      attributes: item.attributes,
    }));
    return { checks: built, checkKeys: built.map(({ key }) => key) };
  }, [items, kind, scope]);
  const undecided = checks.filter(({ key }) => !decisions?.has(key));

  /** Applies one batch's outcome on top of whatever the other batches settled meanwhile. */
  const settle = (
    keys: readonly string[],
    update: (
      decisionsDraft: Map<string, Record<TPermission, boolean>>,
      failuresDraft: Map<string, Error>,
    ) => void,
  ) => {
    setCache((previous) => {
      const same = previous.client === client;
      const nextDecisions = new Map(same ? previous.decisions : undefined);
      const nextFailures = new Map(same ? previous.failures : undefined);
      for (const key of keys) nextFailures.delete(key);
      update(nextDecisions, nextFailures);
      return { client, decisions: nextDecisions, failures: nextFailures };
    });
  };

  // Keyed on the checks asked for, not on what is still undecided: one batch
  // settling must not re-send another batch that just failed.
  useDeepCompareEffect(() => {
    const requestScope = requestScopeRef.current;
    if (!online || requestScope?.client !== client || requestScope.signal.aborted) return;
    const { signal } = requestScope;

    const toRequest = undecided.filter(({ key }) => !inFlightKeysRef.current.has(key));
    for (const batch of chunk(toRequest, MAX_RESOURCES_PER_REQUEST)) {
      const keys = batch.map(({ key }) => key);
      for (const key of keys) inFlightKeysRef.current.add(key);
      // Being checked again: loading, no longer failed.
      settle(keys, () => undefined);

      const configs = batch.map(({ key, id, attributes }) => {
        const config: ResourceConfig = { kind, actionMap, attributes };
        return { key, id, config };
      });
      const resources = configs.map(({ id, config }) =>
        createResourceCheck({ ...config, id, resourceScope: scope }),
      );

      client
        .checkResources({ resources }, { signal })
        .then((response) => {
          if (signal.aborted) return;
          for (const key of keys) inFlightKeysRef.current.delete(key);
          settle(keys, (decisionsDraft) => {
            for (const { key, id, config } of configs) {
              decisionsDraft.set(
                key,
                getPermissions<Record<TPermission, boolean>>({
                  permissionConfigs: [config],
                  data: response,
                  id,
                  scope,
                }),
              );
            }
          });
        })
        .catch((err: unknown) => {
          if (signal.aborted) return;
          for (const key of keys) inFlightKeysRef.current.delete(key);
          const error = toError(err);
          settle(keys, (_decisionsDraft, failuresDraft) => {
            for (const key of keys) failuresDraft.set(key, error);
          });
        });
    }
  }, [client, online, checkKeys]);

  const permissions = Object.fromEntries(
    checks.map(({ key, id }) => [
      id,
      decisions?.get(key) ??
        // No response to read from: every permission comes out `false`.
        getPermissions<Record<TPermission, boolean>>({
          permissionConfigs: [{ kind, actionMap }],
          id,
        }),
    ]),
  );

  const error = undecided.map(({ key }) => failures?.get(key)).find((found) => found !== undefined);

  return {
    permissions,
    isLoading: undecided.some(({ key }) => !failures?.has(key)),
    error,
  };
}
